import React, { useState } from "react";
import CustomInput from "../../../../components/common/CustomInput/CustomInput";
import CustomTable from "../../../../components/common/CustomTable";
import CustomFileUpload from "../../../../components/common/CustomFileUpload/CustomFileUpload";
import CustomDropdown from "../../../../components/common/CustomDropdown/CustomDropdown";
import CustomDatePicker from "../../../../components/common/CustomDatePicker/CustomDatePicker";
import { useToggleState } from "../../../../Hooks/useToggleState";
import CustomModal from "../../../../components/common/Modal";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
   addSingleFleetChildAsync,
   fetchSingleFleetAsync,
   updateSingleFleetChildAsync,
} from "../../../../redux/features/fleetMaster";
import { BsPencilSquare } from "react-icons/bs";
import { backdropLoadingAction } from "../../../../redux/features/helperSlice";
import toast from "react-hot-toast";
import * as Yup from "yup";
import {
   MAX_FILE_SIZE,
   MAX_FILE_SIZE_MESSAGE,
   isValidFileType,
} from "../../../../helpers/fileValidationRules";
import { Form, FormikProvider, useFormik } from "formik";
import { MAX_DATE } from "../../../../Hooks/dateValidationRules";
import MultipleFileUpload from "../../../../components/common/MultipleFileUpload/MultipleFileUpload";

const validationSchema = Yup.object().shape({
   odometerReading: Yup.number().required("Odometer Reading is required"),
   tyreType: Yup.string().required("Tyre Type Name is required"),
   tyreChangeDate: Yup.date()
      .required("Tyre Change Date is required")
      .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
   tyreAmount: Yup.number().required("tyre Amount is required"),
   tyreStationName: Yup.string().required("Tyre Station Name is required"),
   existingTyreBillUpload: Yup.string(),
   tyreBillUpload: Yup.array()
      .of(
         Yup.mixed()
            .required("Tyre Bill is required")
            .test("is-valid-type", "Not a valid document type", (value) => {
               if (value && typeof value === "string") return true;
               return isValidFileType(value && value.name.toLowerCase(), "all");
            })
            .test("is-valid-size", MAX_FILE_SIZE_MESSAGE, (value) => {
               if (value && typeof value === "string") return true;
               return value && value.size <= MAX_FILE_SIZE;
            })
      )
      .when("existingTyreBillUpload", {
         is: (existingTyreBillUpload) => !existingTyreBillUpload,
         then: (schema) => {
            return schema.min(1, "Tyre Bill is required");
         },
         otherwise: (schema) => {
            return schema.min(0);
         },
      }),
});

const initialValues = {
   id: "",
   odometerReading: "",
   tyreType: "",
   tyreChangeDate: "",
   tyreAmount: "",
   tyreStationName: "",
   tyreBillUpload: [],
   existingTyreBillUpload: "",
   removedTyreBillUpload: "",
};

function Tyre({ tyreAdd, selectOptionData }) {
   const params = useParams();
   const dispatch = useDispatch();

   const [action, setAction] = useState("");
   const [isModalVisible, handleShowModal, handleCloseModal] = useToggleState();

   const columns = [
      {
         name: "Odometer Reading",
         selector: (row) => row.odometerReading,
      },
      {
         name: "Tyre Amount",
         selector: (row) => row.tyreAmount,
         sortable: true,
      },
      {
         name: "Tyre Change Date",
         selector: (row) => {
            return row.tyreChangeDate;
         },
         sortable: true,
      },
      {
         name: "Action",
         width: "9rem",
         selector: (row) => row.action,
      },
   ];

   const data = tyreAdd?.map((item) => ({
      ...item,
      action: (
         <button
            className="btn"
            onClick={() => {
               setAction("edit");
               setValues({
                  id: item?.id,
                  odometerReading: item?.odometerReading,
                  tyreType: item?.tyreType,
                  tyreChangeDate: item?.tyreChangeDate,
                  tyreAmount: item?.tyreAmount,
                  tyreStationName: item?.tyreStationName,
                  tyreBillUpload: [],
                  existingTyreBillUpload: item?.tyreBillUpload,
               });
               handleShowModal();
            }}
            type="button"
         >
            <BsPencilSquare
               className="text-primary"
               style={{ height: "1.5rem", width: "1.5rem" }}
            />
         </button>
      ),
   }));

   const handleAddOrUpdateTyre = (formData) => {
      const newData = new FormData();

      const data = {
         fleet_master_id: params?.id,
         model_id: 3,
         tyre_id: formData?.id,

         // bill_upload_along_with_warranty_status[]:File upload,
      };
      const dataArr = {
         tyre_type_id: [formData.tyreType],
         odometer_reading: [formData.odometerReading],
         tyre_amount: [formData.tyreAmount],
         tyre_change_date: [formData.tyreChangeDate],
         tyre_station_name: [formData.tyreStationName],
      };

      Object.keys(data).forEach((item) => {
         newData.append(item, data[item]);
      });
      Object.keys(dataArr).forEach((item) => {
         newData.append(`${item}[]`, JSON.stringify(dataArr[item]));
      });

      if (action === "edit") {
         newData.append("_method", "PATCH");
         newData.append("action_id", 2);
         formData.tyreBillUpload?.forEach?.((item) => {
            newData.append("bill_upload_along_with_warranty_status[]", item);
         });

         newData.append(
            "existing_bill_upload_along_with_warranty_status",
            formData.existingTyreBillUpload
         );
         newData.append(
            "removed_bill_upload_along_with_warranty_status",
            formData.removedTyreBillUpload
         );

         dispatch(backdropLoadingAction(true));
         dispatch(
            updateSingleFleetChildAsync({
               data: newData,
               err: () => {
                  dispatch(backdropLoadingAction(false));
                  toast.error("Something went wrong !");
                  // handleCloseModal();
               },
               done: () => {
                  dispatch(fetchSingleFleetAsync({ id: params?.id }));
                  dispatch(backdropLoadingAction(false));
                  toast.success("Record updated successfully !");
                  handleCloseModal();
               },
            })
         );
      }

      if (action === "add") {
         newData.append("action_id", 1);

         formData.tyreBillUpload?.forEach?.((item) => {
            newData.append("bill_upload_along_with_warranty_status[]", item);
         });

         newData.delete("tyre_id");

         dispatch(backdropLoadingAction(true));
         dispatch(
            addSingleFleetChildAsync({
               data: newData,
               err: () => {
                  dispatch(backdropLoadingAction(false));
                  toast.error("Something went wrong !");
                  // handleCloseModal();
               },
               done: () => {
                  dispatch(fetchSingleFleetAsync({ id: params?.id }));
                  dispatch(backdropLoadingAction(false));
                  toast.success("Record added successfully !");
                  handleCloseModal();
               },
            })
         );
      }
   };

   const formik = useFormik({
      initialValues,
      validationSchema,
      enableReinitialize: true,
      onSubmit: handleAddOrUpdateTyre,
   });

   const {
      errors,
      touched,
      getFieldProps,
      handleSubmit,
      setValues,
      values,
      setFieldValue,
      resetForm,
   } = formik;

   return (
      <>
         <CustomModal
            showModal={isModalVisible}
            handleCloseModal={handleCloseModal}
            modalSize="modal-xl"
            title="Fleet"
            onSubmit={handleSubmit}
            child={
               <FormikProvider value={formik}>
                  <Form noValidate>
                     <div
                        className="tab-pane fade show active"
                        id="3"
                        role="tabpanel"
                        aria-labelledby="3"
                     >
                        <div className="col-lg-12">
                           <div className="card card-primary">
                              <div className="card-header">
                                 <h3 className="card-title">Tyre</h3>
                              </div>
                              <div className="card-body">
                                 <div className={"row"}>
                                    <div className="col-lg-4">
                                       <CustomInput
                                          require
                                          label="Odometer reading"
                                          id="odometerReading"
                                          placeholder="Enter Odometer reading"
                                          {...getFieldProps("odometerReading")}
                                          errors={
                                             Boolean(touched.odometerReading) &&
                                             Boolean(errors.odometerReading)
                                          }
                                          message={errors.odometerReading}
                                       />
                                    </div>
                                    <div className="col-lg-4">
                                       <CustomDropdown
                                          label="Tyre Type"
                                          optionData={selectOptionData}
                                          {...getFieldProps("tyreType")}
                                          onChange={(_, value) =>
                                             setFieldValue("tyreType", value)
                                          }
                                          errors={
                                             Boolean(touched.tyreType) &&
                                             Boolean(errors.tyreType)
                                          }
                                          message={errors.tyreType}
                                       />
                                    </div>
                                    <div className="col-lg-4">
                                       <CustomDatePicker
                                          require
                                          label="Tyre Change Date"
                                          id="tyreChangeDate"
                                          placeholder="Select Tyre Change Date"
                                          {...getFieldProps("tyreChangeDate")}
                                          errors={
                                             Boolean(touched.tyreChangeDate) &&
                                             Boolean(errors.tyreChangeDate)
                                          }
                                          message={errors.tyreChangeDate}
                                       />
                                    </div>
                                    <div className="col-lg-6">
                                       <CustomInput
                                          require
                                          label="Tyre Amount"
                                          id="tyreAmount"
                                          placeholder="Enter Tyre Amount"
                                          {...getFieldProps("tyreAmount")}
                                          errors={
                                             Boolean(touched.tyreAmount) &&
                                             Boolean(errors.tyreAmount)
                                          }
                                          message={errors.tyreAmount}
                                       />
                                    </div>
                                    <div className="col-lg-6">
                                       <CustomInput
                                          require
                                          label="Tyre Station Name"
                                          id="tyreStationName"
                                          placeholder="Enter Tyre Station Name"
                                          {...getFieldProps("tyreStationName")}
                                          errors={
                                             Boolean(touched.tyreStationName) &&
                                             Boolean(errors.tyreStationName)
                                          }
                                          message={errors.tyreStationName}
                                       />
                                    </div>

                                    <div className="col-lg-12">
                                       <MultipleFileUpload
                                          label={"Bill upload along with warranty status"}
                                          onChange={(files) => {
                                             setFieldValue("tyreBillUpload", files);
                                          }}
                                          accepts={[
                                             ".pdf",
                                             ".doc",
                                             ".docx",
                                             ".csv",
                                             ".txt",
                                          ]}
                                          existingValue={values.existingTyreBillUpload}
                                          value={values.tyreBillUpload}
                                          onRemoveExistingFiles={(
                                             existingValues,
                                             removedValues
                                          ) => {
                                             setFieldValue(
                                                "existingTyreBillUpload",
                                                existingValues
                                             );
                                             setFieldValue(
                                                "removedTyreBillUpload",
                                                removedValues
                                             );
                                          }}
                                          errors={Boolean(errors.tyreBillUpload)}
                                          message={errors.tyreBillUpload}
                                       />
                                    </div>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </Form>
               </FormikProvider>
            }
         />

         <CustomTable columns={columns} data={data} />

         <div className="row mt-3">
            <div className="col-lg-6">
               <button
                  type="button"
                  className="btn btn-primary float-left"
                  onClick={() => {
                     setAction("add");
                     resetForm();
                     handleShowModal();
                  }}
               >
                  <i className="fas fa-plus"></i> Add More
               </button>
            </div>
         </div>
      </>
   );
}

export default Tyre;
